import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import {
  midgardClients,
  DATA_DUMP_AGGREGATE_QUERY,
} from "providers/MidgardProvider";
import useQueryWithErrorHandling from "utils/useQuery";
import { useProcessIdOnSingleProcessPage } from "components/CustomGridItems/LotInfoBar/lotInfoUtils";
import { useSettings } from "contexts/SettingsManager/SettingsManager";
import { ONE_HOUR_OF_MS } from "utils/timeUtils";
import { HUBLET_VIEW_DATA_DUMP_POLL_INTERVAL } from "constants/intervals";
import { DATA_DUMP_FOR_HUBLET_QUERY_TITLE } from "contexts/ErrorManager/errorManagerUtils";
import { updateHubletViewRollingWindowDataDump } from "providers/GlobalStateProvider";
import { useDispatch } from "react-redux";
import { useCleanRawDataDump } from "segments/DataDumpGetter/dataDumpGetterUtils";
import { useGeForDataDumpQueryHubletView } from "./hubletViewUtils";
import { useIsTimeRangeSmallEnough } from "segments/DataDumpGetter/DataDumpGetter";
import { useRef, useTransition } from "react";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useInterval } from "react-use";

export default function HubletViewDataDumpRollingWindowGetter() {
  const now = useRef(Date.now());
  useInterval(() => {
    now.current = Date.now();
  }, 1000);
  const { rateRollingTimeWindow } = useSettings();
  const processId = useProcessIdOnSingleProcessPage();

  const geForQuery = useGeForDataDumpQueryHubletView();
  const rateRollingTimeWindowGe = now.current - rateRollingTimeWindow;

  // ge for query = max(rolling window ge, shift start, sku start)
  const geForDataDumpQuery = Math.max(geForQuery, rateRollingTimeWindowGe);

  const variables = {
    ge: String(geForDataDumpQuery),
    lt: String(now.current),
    machemetryIds: processId ? [processId] : [],
  };
  const queryRangeDuration = Number(variables.lt) - Number(variables.ge);
  const isTimeRangeInverted = queryRangeDuration < 0;
  const isTimeRangeZero = queryRangeDuration === 0;

  if (isTimeRangeInverted) {
    console.error(
      `HubletView rolling window time range is inverted! location.pathname: ${
        window.location.pathname
      }, query range ${(queryRangeDuration / ONE_HOUR_OF_MS).toFixed(
        1
      )} hours, variables: ${JSON.stringify(variables)}}`
    );
  }

  const { isTimeRangeSmallEnough } =
    useIsTimeRangeSmallEnough(queryRangeDuration);

  const skip =
    Number(geForDataDumpQuery) === 0 ||
    isTimeRangeInverted ||
    isTimeRangeZero ||
    !processId ||
    !isTimeRangeSmallEnough;

  return skip ? null : <Query {...{ variables }} />;
}

function Query({
  variables,
}: {
  variables: { ge: string; lt: string; machemetryIds: string[] };
}) {
  const dispatch = useDispatch();
  const processId = useProcessIdOnSingleProcessPage();
  const ckey = useCustomerKey();
  const client = midgardClients.get(ckey);
  const cleanRawDataDump = useCleanRawDataDump();
  const [
    {
      general: { multiplyNetworkRequestIntervals },
    },
  ] = useFlightConfig();
  const [, startTransition] = useTransition();

  useQueryWithErrorHandling(
    DATA_DUMP_FOR_HUBLET_QUERY_TITLE,

    DATA_DUMP_AGGREGATE_QUERY,
    {
      client,
      variables,
      onError: err => {
        console.log(
          `HubletViewRollingWindow data dump gone wrong: ${JSON.stringify(
            variables
          )}`
        );
        // useQueryWithErrorHandling will log the error + error code via handleError
      },
      onCompleted: data => {
        if (data?.dataDumpAggregate && processId) {
          startTransition(() => {
            const cleanedDataDump = cleanRawDataDump(data.dataDumpAggregate, [
              processId,
            ]);
            dispatch(updateHubletViewRollingWindowDataDump(cleanedDataDump));
          });
        }
      },
      pollInterval:
        HUBLET_VIEW_DATA_DUMP_POLL_INTERVAL * multiplyNetworkRequestIntervals,
    }
  );
  return null;
}
